import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { ArticleDayreport } from 'src/app/classes/article-dayreport.class';
import { Dayreport } from 'src/app/classes/dayreport.class';
import { Planning } from 'src/app/classes/planning.class';
import { CodaltComponent } from 'src/app/codalt.component';
import { AddMaterialDialogComponent } from 'src/app/pages/day-report/add-material-dialog/add-material-dialog.component';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog-service/confirm-dialog.service';
import { DayreportService } from 'src/app/services/dayreport.service';
import { LocalStorage } from 'src/app/storage.class';
import { DagrapportStatus } from 'src/environments/trackline/status.dagrapport';
import { MaterialType } from '../../../classes/material-type.class';
import { Material } from '../../../classes/material.class';
import { RawMaterial } from '../../../classes/raw-material.class';
import { MaterialService } from '../../../services/material/material.service';
import { DayReportStateService } from '../day-report-state.service';

type LocationDayreportGroup = { open: boolean; items: ArticleDayreport[] };
type ProductionTotalGroup = { rawMaterialId: number; name: string; totalKg: number };

@Component({
    selector: 'app-day-report-materialen',
    templateUrl: './day-report-materialen.component.html',
    styleUrls: ['./day-report-materialen.component.scss'],
    standalone: false,
})
export class DayReportMaterialenComponent extends CodaltComponent implements OnInit {
    saving = false;

    user = LocalStorage.getUser();

    planning: Planning | null = null;
    dayReport: Dayreport | null = null;

    locationDayreports: Record<string, LocationDayreportGroup> = {};
    locationDayreportKeys: string[] = [];

    productionTotals: ProductionTotalGroup[] = [];
    productionTotalKg = 0;

    materials: Material[] = [];
    rawMaterials: RawMaterial[] = [];
    materialTypes: MaterialType[] = [];

    PlanningStatus = this.environment.status.Planning;

    constructor(
        private activatedRoute: ActivatedRoute,
        private stateService: DayReportStateService,
        private dayreportService: DayreportService,
        private dialog: MatDialog,
        private confirmDialogService: ConfirmDialogService,
        private materialService: MaterialService,
        public location: Location
    ) {
        super();
    }

    get readOnly(): boolean {
        return this.dayReport?.status?.mutable !== true || this.declarationLocked;
    }

    startOrAdd() {
        if (this.readOnly && this.canStart) {
            this.startAssignment();
        } else {
            this.add();
        }
    }

    get declarationLocked(): boolean {
        return this.dayReport?.declaration?.status?.mutable === false;
    }

    get canStart(): boolean {
        return this.dayReport?.status_key === DagrapportStatus.DagrapportGepland;
    }

    ngOnInit(): void {
        this.subscriptions.add(
            this.stateService.planning$.subscribe(planning => {
                this.planning = planning;
            })
        );
        this.subscriptions.add(
            this.materialService.getList(true).subscribe(materials => {
                this.materials = materials;
                this.buildProductionTotals();
            })
        );
        this.subscriptions.add(
            this.materialService.getMaterialTypes().subscribe(materialTypes => {
                this.materialTypes = materialTypes || [];
                this.buildProductionTotals();
            })
        );

        this.subscriptions.add(
            this.materialService.getAllRawMaterials().subscribe(rawMaterials => {
                this.rawMaterials = rawMaterials || [];
                this.buildProductionTotals();
            })
        );

        this.subscriptions.add(
            this.stateService.dayReport$.subscribe(dayReport => {
                if (dayReport) {
                    this.dayReport = dayReport;
                    this.groupByLocation();
                    this.buildProductionTotals();
                }
            })
        );
    }

    add() {
        if (!this.planning || !this.dayReport || this.readOnly) {
            return;
        }

        this.subscriptions.add(
            this.dialog
                .open(AddMaterialDialogComponent, {
                    maxWidth: '550px',
                    width: '100vw',
                    maxHeight: '100%',
                    disableClose: true,
                    autoFocus: false,
                    panelClass: 'comment-edit-dialog',
                    data: {
                        planningId: this.planning.id,
                        customerId: this.planning.customer?.id,
                        locations: this.dayReport.article_dayreports?.length
                            ? this.locationDayreportKeys
                            : [
                                  this.planning.location,
                                  this.planning.project.location,
                                  this.planning.mainPlanning?.location,
                              ].filter(Boolean),
                    },
                })
                .afterClosed()
                .subscribe(data => {
                    if (!data) {
                        return;
                    }

                    const { article, material, articleCrm, amount, location } = data;

                    if (!amount || (!articleCrm && (!article || !material))) {
                        return;
                    }

                    const articleDayreport = (articleCrm
                        ? {
                              isNew: true,
                              id: new Date().getTime(),
                              article_crm_id: articleCrm.id,
                              article_crm: articleCrm,
                              raw_materials_ids: [],
                              amount,
                              location,
                          }
                        : {
                              isNew: true,
                              id: new Date().getTime(),
                              article,
                              article_id: article.id,
                              material_id: material.id,
                              raw_materials_ids: material.raw_materials?.map(rawMaterial => rawMaterial.id) ?? [],
                              amount,
                              location,
                          }) as unknown as ArticleDayreport;

                    const locationKey = this.getLocationKey(location);

                    if (!this.locationDayreports[locationKey]) {
                        this.locationDayreports[locationKey] = { open: true, items: [] };
                        this.locationDayreportKeys = [...this.locationDayreportKeys, locationKey];
                    }

                    this.locationDayreports[locationKey].items.push(articleDayreport);

                    this.dayReport!.article_dayreports ??= [];
                    this.dayReport!.article_dayreports.push(articleDayreport);
                    this.buildProductionTotals();

                    this.save();
                })
        );
    }

    save() {
        if (!this.dayReport) {
            return;
        }

        this.saving = true;

        this.dayReport.article_dayreports = this.dayReport.article_dayreports?.map(
            (adr: ArticleDayreport & { isNew?: boolean }) => {
                if (adr.isNew) {
                    const { id, isNew, ...rest } = adr;
                    return rest as ArticleDayreport;
                }

                return adr;
            }
        );

        this.subscriptions.add(
            this.dayreportService.save(this.dayReport).subscribe(response => {
                this.stateService.updateDayReport(response.data);
                this.saving = false;
            })
        );
    }

    startAssignment() {
        if (!this.dayReport || !this.canStart) {
            return;
        }

        this.dayReport.status_key = DagrapportStatus.DagrapportGestart;

        this.save();
    }

    delete(articleDayreport?: ArticleDayreport & { isNew?: boolean }) {
        if (this.readOnly) {
            return;
        }

        const remove = (id: number) => {
            if (!this.dayReport) {
                return;
            }
            this.dayReport.article_dayreports = this.dayReport.article_dayreports?.filter(adr => id !== adr.id);
            this.groupByLocation();
            this.buildProductionTotals();
        };

        const label = articleDayreport?.article
            ? `${articleDayreport.article.group} - ${articleDayreport.article.name}`
            : (articleDayreport?.article_crm?.description ?? articleDayreport?.article_crm?.crm_article_id);

        this.confirmDialogService
            .confirm({
                title: 'Materiaal verwijderen',
                text: `Wil je het materiaal ${label} verwijderen?`,
                confirm: 'Verwijderen',
                cancel: 'Behouden',
            })
            .then(() => {
                if (articleDayreport?.isNew) {
                    remove(articleDayreport.id);
                } else if (articleDayreport) {
                    this.subscriptions.add(
                        this.dayreportService.deleteArticle(articleDayreport.id).subscribe(response => {
                            const removedId = parseInt(response.data, 10);

                            if (removedId == articleDayreport.id) {
                                remove(removedId);
                            }
                        })
                    );
                }
            });
    }

    private groupByLocation() {
        const groups: Record<string, LocationDayreportGroup> = {};

        this.dayReport?.article_dayreports?.forEach(adr => {
            const key = this.getLocationKey(adr.location);

            if (!groups[key]) {
                groups[key] = { open: true, items: [] };
            }

            groups[key].items.push(adr);
        });

        this.locationDayreports = groups;
        this.locationDayreportKeys = Object.keys(groups);
    }

    private getLocationKey(location?: string | null) {
        return location ? location.toLowerCase().trim() : 'overig';
    }

    // Het verbruik per grondstof (bv. thermoplast, nastrooi, primer) hangt aan de markering (article)
    // van de regel, niet aan het materiaal als geheel, dus wordt hier per grondstof opgeteld.
    private buildProductionTotals() {
        const articleLines = (this.dayReport?.article_dayreports ?? []).filter(
            adr => !adr.article_crm_id && adr.article_id
        );

        const totals = new Map<number, ProductionTotalGroup>();

        articleLines.forEach(adr => {
            const material = this.materials.find(m => m.id === adr.material_id);

            (adr.raw_materials_ids ?? []).forEach(rawMaterialId => {
                const rawMaterial = this.rawMaterials.find(rm => rm.id === rawMaterialId);
                const type = this.materialTypes.find(t => t.id === rawMaterial?.material_type_id);
                const consumption =
                    this.getMaterialRawMaterial(material, rawMaterialId)?.pivot?.consumption ??
                    rawMaterial?.consumption;

                if (type?.hidden || !consumption) {
                    return;
                }

                const kg = consumption * (adr.article?.factor ?? 0) * adr.amount;
                const existing = totals.get(rawMaterialId);

                if (existing) {
                    existing.totalKg += kg;
                } else {
                    totals.set(rawMaterialId, { rawMaterialId, name: rawMaterial?.name ?? '', totalKg: kg });
                }
            });
        });

        this.productionTotals = Array.from(totals.values());
        this.productionTotalKg = this.productionTotals.reduce((total, group) => total + group.totalKg, 0);
    }

    getMaterialRawMaterial(material: Material | undefined, rawMaterialId: number): RawMaterial | undefined {
        return material?.raw_materials?.find(rm => rm.id === rawMaterialId);
    }

    protected readonly RawMaterial = RawMaterial;
}
